import { useState } from 'react'

import Textfield from '@components/Textfield'
import Textarea from '@components/Textarea'
import Checkbox from '@components/Checkbox'
import Button from '@components/Button'
import ErrorMessage from '@components/ErrorMessage'
import FileUploader from '@components/FileUploader'
import RequiredSelect from '@components/Select/RequiredSelect'
import { FormSection } from '@components/ContactForm'
import { submitForm } from 'wordpress/actions'
import { formPendingSelector } from 'wordpress/selectors'
import { concatString } from './helpers'

function CustomForm({
    id,
    formFieldGroups,
    submitButtonText,
    consentCheckbox,
    offersCheckbox,
    newsletterCheckbox,
    fileUpload,
    submitForm,
    pending,
    successMessage,
    errorMessage,
    multipleSubmissions,
    parkingLocation,
}) {
    const [formValues, setFormValues] = React.useState({})
    const [emailSubject, setEmailSubject] = React.useState('')
    const [isSent, setIsSent] = React.useState(false)
    const [isFormVisible, setIsFormVisible] = React.useState(true)
    const [fileError, setFileError] = React.useState('')
    const form = React.useRef()
    const isLoading = pending === true
    const isError = isSent && pending instanceof Object
    const isSuccess = isSent && !pending
    const [focusedField, setFocusedField] = useState(null)
    const [focusedFileField, setFocusedFileField] = useState(false)

    const showOptionalLabel = field => !field.required && !formValues[field?.name]?.value

    const updateField = (name, value, label) =>
        setFormValues({
            ...formValues,
            [name]: {
                value,
                label,
            },
        })

    const findSubscriptionEmailField = () => {
        let key

        formFieldGroups.forEach(group => {
            group.fields.forEach(field => {
                if (field.enableSubscriptions) {
                    key = concatString(field.label)
                    return
                }
            })
        })

        return key
    }

    const onSubmit = async event => {
        event.preventDefault()

        const submitData = {
            location: window.location.href,
            values: formValues,
            id: id,
            emailSubject: emailSubject || '',
        }

        if (parkingLocation?.monthlyParkingLocation) {
            submitData.values.location = {
                value: parkingLocation?.monthlyParkingLocation,
                label: 'Location',
            }

            submitData.locationState = parkingLocation?.locationState
            submitData.locationCity = parkingLocation?.locationCity
        }

        if (formValues['newsletters_checked'] || formValues['offers_checked']) {
            const key = findSubscriptionEmailField()

            if (key) {
                submitData.subscriptionEmail = formValues[key].value
            }
        }
        submitForm(submitData)
        setIsSent(true)
    }

    const onSubmitSuccess = () => {
        form.current.reset()
        setFormValues({})
        setIsFormVisible(false)
    }

    const onClickResetForm = () => {
        setIsFormVisible(true)
        setIsSent(false)
    }

    const getFileUploadField = () =>
        fileUpload &&
        fileUpload.enableFileUpload && (
            <div
                className="form-group input text "
                onFocus={() => setFocusedFileField(true)}
                onBlur={() => setFocusedFileField(false)}>
                <FileUploader
                    onFileSelectSuccess={file =>
                        setFormValues({
                            ...formValues,
                            attachment: file,
                        })
                    }
                    onFileSelectError={error => setFileError(error)}
                />
                {!focusedFileField && (
                    <small className="form-text optional">
                        <T>feedback.field.optional</T>
                    </small>
                )}
                {fileError && <ErrorMessage messages={[<T key="file-error">{fileError}</T>]} />}
            </div>
        )

    const getCheckboxes = () =>
        (consentCheckbox.enabled || newsletterCheckbox.enabled || offersCheckbox.enabled) && (
            <>
                {newsletterCheckbox.enabled && (
                    <Checkbox
                        className="ml-0"
                        label={newsletterCheckbox.label}
                        name="newsletters_checked"
                        onChange={event => updateField(event.target.name, event.target.checked, 'Newsletter')}
                    />
                )}
                {offersCheckbox.enabled && (
                    <Checkbox
                        className="ml-0"
                        label={offersCheckbox.label}
                        name="offers_checked"
                        onChange={event => updateField(event.target.name, event.target.checked, 'Offers')}
                    />
                )}
                {consentCheckbox.enabled && <Checkbox className="ml-0" label={consentCheckbox.label} required />}
            </>
        )

    const getFields = () =>
        formFieldGroups.map((group, i) => (
            <React.Fragment key={i}>
                <FormSection title={group.title} showTitle className={i === formFieldGroups.length - 1 && 'message'}>
                    {group.fields && group.fields.map(getField)}
                </FormSection>
            </React.Fragment>
        ))

    const getField = (field, i) => {
        let Element
        let fieldProps
        const name = concatString(field.label)
        const defaultProps = {
            required: field.isRequired,
            name,
        }

        switch (field.type) {
            case 'select':
                Element = RequiredSelect
                fieldProps = {
                    ...defaultProps,
                    options: field.selectOptions.map(option => ({
                        label: option.label,
                        value: concatString(option.label),
                        conditionalFields: option.conditionalFields,
                    })),
                    placeholder: field.label,
                    isClearable: true,
                    onChange: value => {
                        if (value && value.label) {
                            return updateField(name, value.label, field.label)
                        }
                        if (value && value.subSelectLabel) {
                            return updateField(concatString(value.subSelectLabel), value.value, value.subSelectLabel)
                        }
                        // in case clear is triggered
                        if (value === null) {
                            return updateField(name, '', field.label)
                        }
                    },
                    onChangeTextfield: (name, value, label) => updateField(name, value, label),
                    value: formValues[name],
                }

                break
            case 'textarea':
                Element = Textarea
                fieldProps = {
                    ...defaultProps,
                    label: field.label,
                    onChange: event => updateField(name, event, field.label),
                }
                break
            case 'subject':
                Element = Textfield
                fieldProps = {
                    ...defaultProps,
                    label: field.label,
                    onChange: event => setEmailSubject(event.target.value),
                }
                break
            default:
                Element = Textfield
                fieldProps = {
                    ...defaultProps,
                    type: field.type,
                    label: field.label,
                    onChange: event => updateField(name, event.target.value, field.label),
                }
                break
        }

        return (
            <React.Fragment key={i}>
                <div
                    className={`form-group input text ${field.fullWidth ? 'col-sm-12' : 'col-sm-6'}  `}
                    onFocus={() => setFocusedField(i)}
                    onBlur={() => setFocusedField(null)}>
                    <Element {...fieldProps} />
                    {showOptionalLabel(fieldProps) && focusedField !== i && (
                        <small className="form-text optional">
                            <T>feedback.field.optional</T>
                        </small>
                    )}
                </div>
            </React.Fragment>
        )
    }

    React.useEffect(() => {
        if (isSent && isSuccess) {
            onSubmitSuccess()
        }
    }, [isLoading])

    return (
        <>
            {isFormVisible && (
                <form className="text-block contact-form" onSubmit={onSubmit} ref={form}>
                    <fieldset disabled={isLoading}>
                        {getFields()}
                        {getFileUploadField()}
                        {getCheckboxes()}

                        <Button isLoading={isLoading} disabled={!!fileError}>
                            {submitButtonText}
                        </Button>
                    </fieldset>
                </form>
            )}
            {isSuccess && (
                <>
                    <div className="text-block" role="alert" dangerouslySetInnerHTML={{ __html: successMessage }} />
                    {multipleSubmissions.enabled && (
                        <div className="text-block">
                            <p>
                                <Button btnStyle="outline" align="center" onClick={onClickResetForm}>
                                    {multipleSubmissions.resetButtonText}
                                </Button>
                            </p>
                        </div>
                    )}
                </>
            )}
            {isError && (
                <div className="text-block">
                    <span className="text-red" role="alert">
                        {errorMessage}
                    </span>
                </div>
            )}
        </>
    )
}

const mapStateToProps = createStructuredSelector({ pending: formPendingSelector })
export default connect(mapStateToProps, { submitForm })(CustomForm)
